import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_GROUPS_LOAD,
    ACTION_GROUPS_LOAD_BY_ID,
    ACTION_GROUPS_LOAD_FAILURE,
    ACTION_GROUPS_LOAD_SUCCESS,

    ACTION_GROUPS_SAVE,
    ACTION_GROUPS_SAVE_SUCCESS,
    ACTION_GROUPS_SAVE_FAILURE,

    ACTION_GROUPS_CREATE,
    ACTION_GROUPS_CREATE_SUCCESS,
    ACTION_GROUPS_CREATE_FAILURE,

    ACTION_GROUPS_CLEAR,
    ACTION_GROUPS_FILTER,
    ACTION_GROUPS_PERSONNEL_ROLES_FILTER,

    GroupAction
} from '../actions/groups';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface IGroupState {
    formData: Dtos.Group | Dtos.Group[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.Group[];
    formLength: number;
    formFilterPersonnelRoles: IFormFilter;
    formFilteredPersonnelRoles: Dtos.PersonnelRoleSummary[];
    formLengthPersonnelRoles: number;
}

const initialState: IGroupState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0,
    formFilterPersonnelRoles: {
        search: "",
        showInActive: false
    },
    formFilteredPersonnelRoles: [],
    formLengthPersonnelRoles: 0
};


function loading(state: IGroupState, isLoading: boolean): IGroupState {
    const newState: IGroupState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(state: IGroupState,
    formData: Dtos.Group | Dtos.Group[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): IGroupState {

    // if switching between list and details, reset the list
    var isList: boolean = formData instanceof Array ? true : false;
    const formFilterPersonnelRoles = isList ? {
        search: "",
        showInActive: false
    } : state.formFilterPersonnelRoles;

    const newState: IGroupState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        },
        formFilterPersonnelRoles: {
            $set: formFilterPersonnelRoles
        }
    });

    return filterPersonnelRoles(filter(newState, state.formFilter), newState.formFilterPersonnelRoles);
}

function loadFailure(state: IGroupState, responseStatus: Dtos.ResponseStatus): IGroupState {
    const newState: IGroupState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function filter(state: IGroupState, formFilter: IFormFilter): IGroupState {

    const items: Dtos.Group[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (formFilter.search == "" || p.name.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)) : undefined;

    const newState: IGroupState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function filterPersonnelRoles(state: IGroupState, formFilter: IFormFilter): IGroupState {

    let group: Dtos.Group | undefined =
        state.formData instanceof Array ?
            undefined : JSON.parse(JSON.stringify(state.formData));

    let personnelRoles: Dtos.PersonnelRoleSummary[] =
        group ? [...group.personnelRoleSummaries] : [];

    const formLengthPersonnelRoles = personnelRoles ? personnelRoles.length : 0;

    personnelRoles.forEach(item => {
        var personnelRoleSummaries = item.personnelRoleSummaries.filter(p => (p.personnelRole.active == true || formFilter.showInActive) &&
            (
                formFilter.search == ""
                || p.personnelRole.roleDisplay.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1
                || p.personnelRole.personnelDisplay.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1
            )
        );
        item.personnelRoleSummaries = [...personnelRoleSummaries];
    });

    let formFilteredPersonnelRoles = personnelRoles.filter(p => p.personnelRoleSummaries.length > 0);

    const newState: IGroupState = update(state, {
        formFilterPersonnelRoles: {
            $set: formFilter
        },
        formFilteredPersonnelRoles: {
            $set: formFilteredPersonnelRoles
        },
        formLengthPersonnelRoles: {
            $set: formLengthPersonnelRoles
        }
    });

    return newState;
}

function clear(state: IGroupState, propertiesToClear?: string[]): IGroupState {

    if (!propertiesToClear) {
        let formFilter = state.formFilter;
        const newState: IGroupState = update(initialState, {
            formFilter: {
                $set: formFilter
            }
        });
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: IGroupState, isSaving: boolean): IGroupState {
    const newState: IGroupState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: IGroupState,
    formData: Dtos.Group | Dtos.Group[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IGroupState {
    const newState: IGroupState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: IGroupState, responseStatus: Dtos.ResponseStatus): IGroupState {
    const newState: IGroupState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: IGroupState, isCreating: boolean): IGroupState {
    const newState: IGroupState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: IGroupState,
    formData: Dtos.Group,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): IGroupState {
    const newState: IGroupState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: IGroupState, responseStatus: Dtos.ResponseStatus): IGroupState {
    const newState: IGroupState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

const GroupReducer: Reducer<IGroupState> = (state: IGroupState = initialState, action: GroupAction) => {
    switch (action.type) {
        case ACTION_GROUPS_LOAD:
        case ACTION_GROUPS_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_GROUPS_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_GROUPS_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_GROUPS_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_GROUPS_SAVE:
            return saving(state, true);
        case ACTION_GROUPS_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_GROUPS_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_GROUPS_CREATE:
            return creating(state, true);
        case ACTION_GROUPS_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_GROUPS_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_GROUPS_FILTER:
            return filter(state, action.formFilter);
        case ACTION_GROUPS_PERSONNEL_ROLES_FILTER:
            return filterPersonnelRoles(state, action.formFilter);
    }

    return state;
}

export default GroupReducer;